import React, { createContext, useContext, useState, useCallback, useMemo } from "react";

const LogContext = createContext();

export const useLogs = () => {
  const context = useContext(LogContext);
  if (!context) {
    throw new Error("useLogs must be used within a LogProvider");
  }
  return context;
};

const DEFAULT_CHANNEL = "byosync";

const normalizeChannel = (channel) => {
  const c = String(channel || DEFAULT_CHANNEL).trim().toLowerCase();
  if (!c) return DEFAULT_CHANNEL;
  // common typo / alias
  if (c === "adhar") return "aadhaar";
  return c;
};

const autoChannelForMessage = (message) => {
  const m = String(message || "").toLowerCase();
  // Heuristics (kept intentionally simple)
  if (m.includes("otp") || m.includes("aadhar") || m.includes("aadhaar")) return "aadhaar";
  if (m.includes("bch") || m.includes("ecc") || m.includes("helper") || m.includes("mpc"))
    return "mpc";
  if (m.includes("face") || m.includes("scan") || m.includes("facemesh")) return "apple_face_id";
  return DEFAULT_CHANNEL;
};

const shouldSkipLog = (msg) => {
  const m = String(msg || "");
  // Suppress noisy / redundant messages (per request)
  if (m.includes("✅ Registration data loaded from localStorage")) return true;
  if (m.includes("Device Already registered Please Verify Your Face")) return true;
  return false;
};

export const LogProvider = ({ children }) => {
  // Backward compatible "all logs" (what the old UI used)
  const [logs, setLogs] = useState([]);

  // New: channel-based logs
  const [logsByChannel, setLogsByChannel] = useState({
    byosync: [],
    aadhaar: [],
    apple_face_id: [],
    mpc: [],
  });

  // Optional per-channel metadata for UI (e.g., Aadhaar photo preview, Apple distance hash)
  const [channelMeta, setChannelMetaState] = useState({
    byosync: {},
    aadhaar: { photo: null },
    apple_face_id: { distanceHash: null, frame: null },
    mpc: { hash1: null, hash2: null, hash3: null, frame: null },
  });

  /**
   * addLog(message, channel?)
   * - Existing calls: addLog("hello") continue to work
   * - New calls: addLog("hello", "mpc")
   */
  const addLog = useCallback((message, channel) => {
    const msg = String(message ?? "");
    if (shouldSkipLog(msg)) return;

    const selectedChannel = normalizeChannel(channel || autoChannelForMessage(msg));
    const line = msg;

    setLogs((prev) => {
      // de-dup consecutive identical logs
      if (prev.length > 0 && prev[prev.length - 1] === line) return prev;
      return [...prev, line];
    });
    setLogsByChannel((prev) => ({
      ...prev,
      [selectedChannel]: (() => {
        const arr = prev[selectedChannel] || [];
        if (arr.length > 0 && arr[arr.length - 1] === line) return arr;
        return [...arr, line];
      })(),
    }));
  }, []);

  const clearLogs = useCallback((channel) => {
    const selectedChannel = normalizeChannel(channel);
    setLogsByChannel((prev) => ({ ...prev, [selectedChannel]: [] }));
  }, []);

  const setChannelMeta = useCallback((channel, next) => {
    const selectedChannel = normalizeChannel(channel);
    setChannelMetaState((prev) => {
      const prevForChannel = prev[selectedChannel] || {};
      const nextForChannel =
        typeof next === "function" ? next(prevForChannel) : next;
      return { ...prev, [selectedChannel]: { ...prevForChannel, ...nextForChannel } };
    });
  }, []);

  const clearChannelMeta = useCallback((channel) => {
    const selectedChannel = normalizeChannel(channel);
    setChannelMetaState((prev) => ({ ...prev, [selectedChannel]: {} }));
  }, []);

  const clearAllLogs = useCallback(() => {
    setLogs([]);
    setLogsByChannel({
      byosync: [],
      aadhaar: [],
      apple_face_id: [],
      mpc: [],
    });
    setChannelMetaState({
      byosync: {},
      aadhaar: { photo: null },
      apple_face_id: { distanceHash: null, frame: null },
      mpc: { hash1: null, hash2: null, hash3: null, frame: null },
    });
  }, []);

  const value = useMemo(
    () => ({
      logs,
      logsByChannel,
      channelMeta,
      addLog,
      clearLogs,
      clearAllLogs,
      setChannelMeta,
      clearChannelMeta,
    }),
    [logs, logsByChannel, channelMeta, addLog, clearLogs, clearAllLogs, setChannelMeta, clearChannelMeta]
  );

  return (
    <LogContext.Provider value={value}>
      {children}
    </LogContext.Provider>
  );
};
